import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCompanyData } from '../hooks/useCompanyData';
import { ChartBarIcon, ClockIcon, CurrencyEuroIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

export default function DashboardPage() {
  const [dateRange, setDateRange] = useState('week');

  // Roep de RPC functie aan voor de company_id
  const { data: companyId, isLoading: isLoadingCompanyId } = useQuery<string | null>({
    queryKey: ['companyId'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_current_user_company_id');
      if (error) throw error;
      return data as string | null;
    },
    staleTime: Infinity,
  });

  // 🆕 FIX: Haal huidige user op
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: companyData, isLoading: isLoadingCompanyData, isError, error } = useCompanyData(companyId || undefined, dateRange);

  // 🆕 FIX: Correcte userName berekening - zoek employee van ingelogde user
  const userName = useMemo(() => {
    if (!currentUser || !companyData?.employees) return 'Gebruiker';
    const currentEmployee = companyData.employees.find(
      emp => emp.user_id === currentUser.id
    );
    return currentEmployee?.full_name || 'Gebruiker';
  }, [currentUser, companyData?.employees]);

  const calculateHours = (startTime: string | null, endTime: string | null, breakDuration: number | undefined | null) => {
    if (!startTime || !endTime) return { minutes: 0, net: '00:00' };
    const parse = (timeStr: string) => (parseInt(timeStr.split(':')[0], 10) * 60) + parseInt(timeStr.split(':')[1], 10);
    const startMins = parse(startTime);
    const endMins = parse(endTime);
    const breakMins = breakDuration || 0;
    let totalMins = endMins >= startMins ? endMins - startMins : (24 * 60) - startMins + endMins;
    const netMins = Math.max(0, totalMins - breakMins);
    const format = (mins: number) => `${Math.floor(mins / 60).toString().padStart(2, '0')}:${Math.round(mins % 60).toString().padStart(2, '0')}`;
    return { minutes: netMins, net: format(netMins) };
  };

  const dashboardStats = useMemo(() => {
    const timeEntries = companyData?.timeEntries || [];
    const projects = companyData?.projects || [];
    const initialStats = { totalHours: 0, billableHours: 0, totalEarnings: 0 };
    
    const stats = timeEntries.reduce((acc, entry) => {
      const entryRate = entry.hourly_rate;
      const employeeRate = entry.employees?.hourly_rate;
      const projectRate = entry.projects?.hourly_rate;
      
      const rate = entryRate || projectRate || employeeRate || 0;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Rate calculation for entry:', entry.id, {
          entryRate,
          projectRate,
          employeeRate,
          finalRate: rate,
          project: entry.projects?.name,
          employee: entry.employees?.full_name
        });
      }
      
      const { minutes } = calculateHours(entry.start_time, entry.end_time, entry.break_duration);
      const hours = minutes / 60;

      acc.totalHours += hours;
      if (entry.is_billable) acc.billableHours += hours;
      
      if (rate > 0) {
        acc.totalEarnings += hours * rate;
      }
      
      return acc;
    }, initialStats);
    
    const activeProjects = projects.filter(p => p.status === 'active').length;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Final dashboard stats:', {
        totalHours: stats.totalHours,
        billableHours: stats.billableHours,
        totalEarnings: stats.totalEarnings,
        entriesCount: timeEntries.length
      });
    }
    
    return {
      totalHours: stats.totalHours.toFixed(1),
      billableHours: stats.billableHours.toFixed(1),
      totalEarnings: stats.totalEarnings.toFixed(2),
      activeProjects,
    };
  }, [companyData]);

  const isLoading = isLoadingCompanyId || isLoadingCompanyData;

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-300">Dashboard laden...</span>
      </div>
    );
  }

  if (isError || !companyId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-red-800 dark:text-red-200">Toegangsfout</h3>
              <p className="text-red-600 dark:text-red-300 mt-1">
                Geen bedrijf gevonden of er is een fout opgetreden: {error?.message}.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Dashboard Overzicht</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">Welkom terug, {userName}</p>
        </div>
        
        {/* Dropdown met custom arrow - Responsive */}
        <div className="relative w-full sm:w-auto">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="w-full sm:w-auto appearance-none pl-4 pr-11 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
          >
            <option value="today">Vandaag</option>
            <option value="week">Deze week</option>
            <option value="month">Deze maand</option>
            <option value="year">Dit jaar</option>
          </select>
          <ChevronDownIcon className="h-5 w-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
        </div>
      </div>
      
      {/* Stats Grid - Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 my-6 md:my-8">
        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
          <ClockIcon className="h-6 w-6 text-blue-500 mb-2" />
          <p className="text-sm md:text-base">Totale uren</p>
          <p className="text-xl md:text-2xl font-bold">{dashboardStats.totalHours}u</p>
        </div>
        
        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
          <CurrencyEuroIcon className="h-6 w-6 text-green-500 mb-2" />
          <p className="text-sm md:text-base">Facturabele uren</p>
          <p className="text-xl md:text-2xl font-bold">{dashboardStats.billableHours}u</p>
        </div>
        
        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
          <CurrencyEuroIcon className="h-6 w-6 text-purple-500 mb-2" />
          <p className="text-sm md:text-base">Verdiensten</p>
          <p className="text-xl md:text-2xl font-bold">€{dashboardStats.totalEarnings}</p>
          {process.env.NODE_ENV === 'development' && (
            <p className="text-xs text-gray-500 mt-1">
              Debug: {companyData?.timeEntries?.length || 0} entries
            </p>
          )}
        </div>
        
        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
          <ChartBarIcon className="h-6 w-6 text-indigo-500 mb-2" />
          <p className="text-sm md:text-base">Actieve projecten</p>
          <p className="text-xl md:text-2xl font-bold">{dashboardStats.activeProjects}</p>
        </div>
      </div>
      
      {/* Recent Activities */}
      <div>
        <h2 className="text-xl md:text-2xl font-bold mb-4">Recente activiteiten</h2>
        <div className="space-y-3 md:space-y-4">
          {companyData?.timeEntries && companyData.timeEntries.length > 0 ? (
            companyData.timeEntries.slice(0, 5).map(entry => {
              const { net } = calculateHours(entry.start_time, entry.end_time, entry.break_duration);
              const entryRate = entry.hourly_rate || entry.projects?.hourly_rate || entry.employees?.hourly_rate || 0;
              const { minutes } = calculateHours(entry.start_time, entry.end_time, entry.break_duration);
              const entryEarnings = entryRate > 0 ? (minutes / 60) * entryRate : 0;
              
              return (
                <div key={entry.id} className="bg-white p-3 md:p-4 rounded-lg shadow flex justify-between items-center">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm md:text-base truncate">{entry.projects?.name}</p>
                    <p className="text-xs md:text-sm text-gray-600">
                      {entry.employees?.full_name} - {new Date(entry.date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}
                    </p>
                    {entryRate > 0 && (
                      <p className="text-xs text-gray-500">
                        €{entryRate}/uur • €{entryEarnings.toFixed(2)}
                      </p>
                    )}
                  </div>
                  <div className={`font-bold text-base md:text-lg p-2 rounded whitespace-nowrap ml-2 ${entry.is_billable ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100'}`}>
                    {net}u
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-white p-4 rounded-lg shadow text-center text-gray-500">
              <p className="text-sm md:text-base">Geen recente activiteiten gevonden in deze periode.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}